/**
 * AppShell — top-level shell that wraps all authenticated routes.
 *
 * Resolves session via useAppSession() (one backend call at startup).
 *   loading          → spinner
 *   bootstrapRequired → BootstrapPage (first Super Admin setup)
 *   session exists   → sidebar limited to employee's workflow memberships
 *   no session       → DtOsLogin
 *
 * No authorization logic here — the backend decides everything.
 */

import { useState }         from 'react'
import { NavMenu }          from '@shopify/app-bridge-react'
import { Outlet }           from 'react-router-dom'
import { Suspense }         from 'react'
import { Spinner, InlineStack } from '@shopify/polaris'
import { SkeletonCard }     from '../components/ui/SkeletonCard'
import { useAppSession, SessionContext } from '../hooks/useAppSession'
import type { EmployeeSession }         from '../hooks/useAppSession'
import { DtOsLogin }        from '../pages/DtOsLogin'
import { BootstrapPage }    from '../pages/BootstrapPage'

// Canonical sidebar order — mirrors WORKFLOW_DISPLAY_ORDER on the backend.
// Only workflows the employee is a member of are shown; admins see all.
const WORKFLOW_NAV: Record<string, { label: string; href: string }> = {
  confirmation:          { label: 'Confirmation',          href: '/workspace/confirmation' },
  shipping_confirmation: { label: 'Shipping Confirmation', href: '/workspace/shipping-confirmation' },
  customer_service:      { label: 'Customer Service',      href: '/workspace/customer-service' },
  sales:                 { label: 'Sales',                 href: '/workspace/sales-team' },
  delivery_tracking:     { label: 'Delivery Tracking',     href: '/workspace/delivery-tracking' },
}

export function AppShell() {
  // When login or bootstrap succeeds, the session is passed directly here —
  // avoids a second /auth/session round-trip.
  const [loginSession, setLoginSession] = useState<EmployeeSession | null>(null)

  const { loading, session: resolvedSession, bootstrapRequired } = useAppSession()
  const session = loginSession ?? resolvedSession

  if (loading) {
    return (
      <div style={{ padding: 32 }}>
        <InlineStack align="center">
          <Spinner accessibilityLabel="Loading session" />
        </InlineStack>
      </div>
    )
  }

  if (!session) {
    if (bootstrapRequired) {
      return <BootstrapPage onComplete={setLoginSession} />
    }
    return <DtOsLogin onLogin={setLoginSession} />
  }

  const memberWorkflows = session.memberships.map(m => m.workflowId)
  const isAdmin         = session.tenantRole === 'admin' || session.tenantRole === 'owner'

  return (
    <>
      <NavMenu>
        <a href="/" rel="home">DTLab</a>
        <a href="/boarding">Boarding</a>
        <a href="/workspace-hub">Workspace</a>
        {isAdmin && <a href="/admin">Administration</a>}
        {/* Operations — shown to all employees; route is protected by ModuleGuard */}
        {!isAdmin && <a href="/workspace/operations">Operations</a>}
        {/* Membership workflows — filtered to what the employee belongs to */}
        {!isAdmin && Object.entries(WORKFLOW_NAV)
          .filter(([wfId]) => memberWorkflows.includes(wfId))
          .map(([wfId, nav]) => (
            <a key={wfId} href={nav.href}>{nav.label}</a>
          ))}
      </NavMenu>
      <SessionContext.Provider value={session}>
        <Suspense fallback={<SkeletonCard />}>
          <Outlet context={{ session }} />
        </Suspense>
      </SessionContext.Provider>
    </>
  )
}
